/* v8 ignore start */
import {v4 as uuidv4} from "uuid";

import {BaseModel} from "@/lib/core/baseModel";
import {LottieAnimationModel} from "@/lib/lottie/animation/lottieAnimationModel";
import {TLottieAssetJson} from "@/lib/lottie/assets/lottieAssetModelResolver";
import {LottieImageAssetModel} from "@/lib/lottie/assets/lottieImageAssetModel";
import {LottiePrecompositionModel} from "@/lib/lottie/assets/lottiePrecompositionModel";
import {LottieImageLayerModel} from "@/lib/lottie/layers/lottieImageLayerModel";
import {LottiePrecompositionLayerModel} from "@/lib/lottie/layers/lottiePrecompositionLayerModel";
import {LottieShapeLayerModel, TRGB} from "@/lib/lottie/layers/lottieShapeLayerModel";
import {LottieSolidColorLayerModel} from "@/lib/lottie/layers/lottieSolidColorLayerModel";
import {LottieTextLayerModel} from "@/lib/lottie/layers/lottieTextLayerModel";
import {Fit} from "@/lib/lottie/layers/photoEffects";
import {LottieFontModel} from "@/lib/lottie/text/lottieFontModel";
import {AnimatedSegmentTransitionModel} from "@/models/animatedSegmentTransitionModel";
import {BackgroundModel} from "@/models/backgroundModel";
import {PhotoModel} from "@/models/photoModel";

interface IPhotoRewriteMapping {
  newId: string;
  photo: PhotoModel;
  scaleX: number;
  scaleY: number;
}

interface IPhotoMapping {
  photo: PhotoModel;
  scaleX: number;
  scaleY: number;
}

export class AnimatedSegmentModel extends BaseModel<AnimatedSegmentModel.IJson> {
  public get id(): Maybe<number> {
    return this.json.id;
  }

  public get width(): number {
    return this.lottieAnimation.width;
  }

  public get height(): number {
    return this.lottieAnimation.height;
  }

  public get lottieAnimation(): LottieAnimationModel {
    return new LottieAnimationModel(this.json.lottieAnimation);
  }

  // The segment can be used only for intro
  public get isIntro(): boolean {
    return this.json.isIntro;
  }

  public deepCopy(): AnimatedSegmentModel {
    const layoutPageJson = JSON.parse(JSON.stringify(this.toObject()));

    return new AnimatedSegmentModel(layoutPageJson).resetUuid();
  }

  public get durationInFrames(): number {
    return this.json.durationInFrames;
  }

  public get photoItemCount(): number {
    return this.photosItems.length;
  }

  // Not fully photoItems since they do not have position
  public get photosItems() {
    return this.lottieAnimation.assets
      .select((asset) => asset instanceof LottieImageAssetModel && asset.isPhoto)
      .toArray() as LottieImageAssetModel[];
  }

  public get textItemCount(): number {
    let textItemCount = 0;

    this.lottieAnimation.preCompositionAssets
      .toArray()
      .forEach((assetLayer) => (textItemCount += assetLayer.textLayers.length));

    return textItemCount;
  }

  public get hasTextItems(): boolean {
    return this.textItemCount > 0;
  }

  public get assetObjects(): TLottieAssetJson[] {
    return this.lottieAnimation.assetObjects;
  }

  public get layerObjects(): LottiePrecompositionLayerModel.IJson[] {
    return this.lottieAnimation.layerObjects;
  }

  public get fontList(): LottieFontModel.IJson[] {
    return this.lottieAnimation.fonts?.list ?? [];
  }

  public get hasDynamicBackground(): boolean {
    return this.json.hasDynamicBackground;
  }

  public setPosition(newPosition: number): this {
    const newLottieAnimation = this.lottieAnimation.updateLayersByType(LottiePrecompositionLayerModel, (layerModel) =>
      layerModel.update({ind: newPosition})
    );

    return this.update({
      position: newPosition,
      lottieAnimation: newLottieAnimation.toObject(),
    });
  }

  public addPrefixId(prefix: string) {
    return this.updateAnimation(this.lottieAnimation.addPrefixId(prefix));
  }

  // TODO: Extract logic into lottieAnimation
  public setStartTimeInFrames(newStartTimeInFrames: number): this {
    const newLottieAnimation = this.lottieAnimation
      .update({
        ip: newStartTimeInFrames,
        op: newStartTimeInFrames + this.durationInFrames,
      })
      .updateLayersByType(LottiePrecompositionLayerModel, (layerModel) => {
        const offset = newStartTimeInFrames - layerModel.startTime;

        return layerModel.offsetTimingBy(offset);
      });

    return this.update({
      lottieAnimation: newLottieAnimation.toObject(),
    });
  }

  // Will adjust duration of the nested components
  public setDuration(newDurationInFrames: number): this {
    if (this.durationInFrames === newDurationInFrames) {
      return this;
    }

    const timeFactor = newDurationInFrames / this.durationInFrames;
    const newOutPoint = this.json.lottieAnimation.ip + newDurationInFrames;

    const newLottieAnimation = this.lottieAnimation
      .updateLayersByType(LottiePrecompositionLayerModel, (layerModel) => layerModel.update({op: newOutPoint}))
      .updateLayersByType(LottiePrecompositionLayerModel, (layerModel) => layerModel.update({op: newOutPoint}))
      .updateAssetsByType(LottiePrecompositionModel, (layerModel) =>
        layerModel.updateLayers((layerModel) => layerModel.adjustTimingBy(timeFactor))
      );

    return this.update({
      durationInFrames: newDurationInFrames,
      lottieAnimation: newLottieAnimation.toObject(),
    });
  }

  public fadeOut(durationInFrames: number): this {
    return this.updateAnimation(
      this.lottieAnimation.updateLayersByType(LottiePrecompositionLayerModel, (layerModel) =>
        layerModel.update({
          ks: layerModel.transform.applyFadeOut(durationInFrames, layerModel.outPoint).toObject(),
        })
      )
    );
  }

  public fadeIn(durationInFrames: number): this {
    return this.updateAnimation(
      this.lottieAnimation.updateLayersByType(LottiePrecompositionLayerModel, (layerModel) =>
        layerModel.update({
          ks: layerModel.transform.applyFadeIn(durationInFrames, layerModel.inPoint).toObject(),
        })
      )
    );
  }

  public slideOut(durationInFrames: number, transitionType: AnimatedSegmentTransitionModel.Type): this {
    const newLottieAnimation = this.lottieAnimation.updateLayersByType(LottiePrecompositionLayerModel, (layerModel) =>
      layerModel.update({
        ks: layerModel.transform
          .applyFullScreenSlideOut(durationInFrames, layerModel.outPoint, transitionType)
          .toObject(),
      })
    );

    return this.update({
      lottieAnimation: newLottieAnimation.toObject(),
    });
  }

  public slideIn(durationInFrames: number, transitionType: AnimatedSegmentTransitionModel.Type): this {
    const newLottieAnimation = this.lottieAnimation.updateLayersByType(LottiePrecompositionLayerModel, (layerModel) =>
      layerModel.update({
        ks: layerModel.transform
          .applyFullScreenSlideIn(durationInFrames, layerModel.inPoint, transitionType)
          .toObject(),
      })
    );

    return this.update({
      lottieAnimation: newLottieAnimation.toObject(),
    });
  }

  public placePhotos(photos: PhotoModel[]): this {
    if (photos.length < 1) {
      return this;
    }

    const lottieImageIdToPhotoIdMap = new Map<string, IPhotoRewriteMapping>();
    const lottieNewImageIdToPhotoIdMap = new Map<string, IPhotoRewriteMapping>();
    const parentLayerToUpdateMap = new Map<string, IPhotoMapping>();
    const secondLevelParentLayerToUpdateMap = new Map<string, IPhotoMapping>();

    let count = 0;

    let newLottieAnimation = this.lottieAnimation
      .updateAssetsByType(LottieImageAssetModel, (assetModel) => {
        if (assetModel.isBackground) return assetModel;
        if (assetModel.isStatic) return assetModel;

        const photo = photos[count];

        if (!photo) return assetModel;

        const newId = `photo_${photo.id}`;

        const scaleX = this.json.hasAutoFitPhotoStrategy ? photo.largestThumbnailWidth / assetModel.width : 1;
        const scaleY = this.json.hasAutoFitPhotoStrategy ? photo.largestThumbnailHeight / assetModel.height : 1;

        lottieImageIdToPhotoIdMap.set(assetModel.id, {newId, photo, scaleX, scaleY});
        lottieNewImageIdToPhotoIdMap.set(newId, {newId, photo, scaleX, scaleY});

        count += 1;

        return assetModel.update({
          id: newId,
          u: "",
          p: photo.url,
          w: Math.round(photo.largestThumbnailWidth),
          h: Math.round(photo.largestThumbnailHeight),
        });
      })
      .updateAssetsByType(LottiePrecompositionModel, (assetModel) =>
        assetModel.updateLayersByType(LottieImageLayerModel, (layerModel) => {
          const mapping = lottieImageIdToPhotoIdMap.get(layerModel.refId);

          if (mapping) {
            parentLayerToUpdateMap.set(assetModel.id, {
              photo: mapping.photo,
              scaleX: mapping.scaleX,
              scaleY: mapping.scaleY,
            });

            return layerModel.update({refId: mapping.newId, nm: `Image ${mapping.newId}`}).fitPhoto();
          } else {
            return layerModel;
          }
        })
      )
      .updateAssetsByType(LottiePrecompositionModel, (assetModel) => {
        let mapping: Maybe<IPhotoMapping>;

        // Done like this to update 2 layers at once
        assetModel.layers.toArray().forEach((layerModel) => {
          if (layerModel instanceof LottiePrecompositionLayerModel && parentLayerToUpdateMap.get(layerModel.refId)) {
            mapping = parentLayerToUpdateMap.get(layerModel.refId);
          }
        });

        if (mapping) {
          const localMapping = mapping; // re-assign to avoid TS error

          // TODO: Very custom code for specific template, consider having a metadata like siblingLayerToUpdateMap
          return assetModel
            .updateLayersByType(LottiePrecompositionLayerModel, (layerModel) => {
              const photoWidth = layerModel.width * localMapping.scaleX;
              const photoHeight = layerModel.height * localMapping.scaleY;

              const anchorX = photoWidth / 2;
              const anchorY = photoHeight / 2;

              secondLevelParentLayerToUpdateMap.set(assetModel.id, {
                photo: localMapping.photo,
                scaleX: localMapping.scaleX,
                scaleY: localMapping.scaleY,
              });

              return layerModel.update({
                w: photoWidth,
                h: photoHeight,
                ks: layerModel.transform
                  .update({
                    a: {a: 0, k: [anchorX, anchorY, 0], ix: 1, l: 2},
                    p: {a: 0, k: [anchorX, anchorY, 0], ix: 2},
                  })
                  .toObject(),
              });
            })
            .updateLayersByType(LottieSolidColorLayerModel, (layerModel) => {
              const photoWidth = layerModel.width * localMapping.scaleX;
              const photoHeight = layerModel.height * localMapping.scaleY;
              const anchorX = photoWidth / 2;
              const anchorY = photoHeight / 2;

              return layerModel.update({
                sw: photoWidth,
                sh: photoHeight,
                ks: layerModel.transform
                  .update({
                    a: {a: 0, k: [anchorX, anchorY, 0], ix: 1, l: 2},
                    p: {a: 0, k: [anchorX, anchorY, 0], ix: 2},
                  })
                  .toObject(),
              });
            });
        } else {
          return assetModel;
        }
      })
      .updateAssetsByType(LottiePrecompositionModel, (assetModel) =>
        assetModel.updateLayersByType(LottiePrecompositionLayerModel, (layerModel) => {
          const mapping = secondLevelParentLayerToUpdateMap.get(layerModel.refId);

          if (mapping) {
            const photoWidth = layerModel.width * mapping.scaleX;
            const photoHeight = layerModel.height * mapping.scaleY;
            const anchorX = photoWidth / 2;
            const anchorY = photoHeight / 2;

            const damping = 0; // TODO: Apply based on metadata
            const dampedScaleX = 1 + (mapping.scaleX - 1) * damping;
            const dampedScaleY = 1 + (mapping.scaleY - 1) * damping;

            // Additional operation to bring scale closer to 1
            const finalScaleX = Math.sqrt(dampedScaleX);
            const finalScaleY = Math.sqrt(dampedScaleY);

            return layerModel.update({
              w: photoWidth,
              h: photoHeight,
              ks: layerModel.transform
                .update({
                  a: {a: 0, k: [anchorX, anchorY, 0], ix: 1, l: 2}, // TODO: Overwrite anchor point occurs here
                  p: layerModel.transform.position?.scale(finalScaleX, finalScaleY).toObject(),
                })
                .toObject(),
            });
          } else {
            return layerModel;
          }
        })
      );

    if (this.json.hasDynamicPhotoEffects) {
      // Animates parent image layers
      newLottieAnimation = newLottieAnimation.updateAssetsByType(LottiePrecompositionModel, (assetModel) =>
        assetModel.updateLayersByType(LottieImageLayerModel, (layerModel) => {
          const mapping = lottieNewImageIdToPhotoIdMap.get(layerModel.refId);

          if (mapping) {
            let parentLayerModel: Maybe<LottiePrecompositionLayerModel>;

            newLottieAnimation.assets.toArray().forEach((assetModel) => {
              if (assetModel instanceof LottiePrecompositionModel) {
                assetModel.layers.toArray().forEach((layerModel) => {
                  if (
                    layerModel instanceof LottiePrecompositionLayerModel &&
                    parentLayerToUpdateMap.get(layerModel.refId)
                  ) {
                    parentLayerModel = layerModel;
                  }
                });
              }
            });

            if (parentLayerModel) {
              return layerModel.fitAnimatedPhoto(mapping.photo, parentLayerModel.width, parentLayerModel.height);
            } else {
              return layerModel;
            }
          } else {
            return layerModel;
          }
        })
      );
    }

    return this.update({lottieAnimation: newLottieAnimation.toObject()});
  }

  public get hasVisibleBackground(): boolean {
    // computed by finding if there are photos with Fit.Contain and there is one photo
    const photosWithContainFit = this.lottieAnimation.preCompositionAssets.reduce(
      0,
      (memo, assetLayer) =>
        (memo += assetLayer.layers.select(
          (layer) => layer instanceof LottieImageLayerModel && layer.metadata?.fit === Fit.Contain
        ).length)
    );

    return this.photoItemCount === 1 && photosWithContainFit > 0;
  }

  public replaceTextWithAutoFit(text: string): this {
    const modifiedCompositionLayers: Map<string, LottieTextLayerModel> = new Map();

    let newLottieAnimation = this.lottieAnimation.updateAssetsByType(LottiePrecompositionModel, (assetModel) =>
      assetModel.updateLayersByType(LottieTextLayerModel, (textLayerModel) => {
        const dimensions = this.getTextLayerDimensions(assetModel);

        if (dimensions) {
          const newTextLayer = textLayerModel.replaceWithAutoFit(text, dimensions.width, dimensions.height);

          modifiedCompositionLayers.set(assetModel.id, newTextLayer);

          return newTextLayer;
        } else {
          return textLayerModel;
        }
      })
    );

    if (modifiedCompositionLayers.size > 0) {
      newLottieAnimation = newLottieAnimation.updateAssetsByType(LottiePrecompositionModel, (assetModel) => {
        const textLayer = modifiedCompositionLayers.get(assetModel.id);

        if (textLayer) {
          const shadowTextLayer = new LottieTextLayerModel(textLayer.deepClonedObject())
            .update({
              ks: textLayer.transform.offsetPosition(2.5, 2.5).toObject(),
            })
            .changeColor([60 / 255, 60 / 255, 60 / 255])
            .update({nm: `${textLayer.name} - shadow`});

          return assetModel.addLayer(shadowTextLayer);
        } else {
          return assetModel;
        }
      });
    }

    return this.update({lottieAnimation: newLottieAnimation.toObject()});
  }

  public addGradientBackgroundFromPhoto(colors: TRGB[]): this {
    return this.updateAnimation(
      this.lottieAnimation.updateAssetsByType(LottiePrecompositionModel, (assetModel) => {
        if (assetModel.isBackgroundItem) {
          return assetModel.addLayer(LottieShapeLayerModel.buildGradientLayer(colors, this.durationInFrames));
        } else {
          return assetModel;
        }
      })
    );
  }

  public addImageBackground(background: BackgroundModel): this {
    const newAsset: TLottieAssetJson = {
      id: "background_1",
      e: 0,
      h: background.largestThumbnailHeight,
      w: background.largestThumbnailWidth,
      meta: {
        type: "background",
        uuid: uuidv4(),
      },
      p: background.largestUrl.url,
    };

    const scale = Math.max(1920 / background.largestThumbnailWidth, 1080 / background.largestThumbnailHeight) * 100;

    const layer = {
      ddd: 0,
      ind: 1,
      ty: 2,
      nm: "background.jpg",
      cl: "jpg",
      refId: "background_1",
      sr: 1,
      ks: {
        o: {a: 0, k: 100, ix: 11},
        r: {a: 0, k: 0, ix: 10},
        p: {a: 0, k: [1920 / 2, 1080 / 2, 0], ix: 2, l: 2},
        a: {a: 0, k: [background.largestThumbnailWidth / 2, background.largestThumbnailHeight / 2, 0], ix: 1, l: 2},
        s: {a: 0, k: [scale, scale, 100], ix: 6, l: 2},
      },
      ao: 0,
      ip: 0,
      op: this.durationInFrames,
      st: 0,
      bm: 0,
    };

    let newLottieAnimation = this.lottieAnimation.update({assets: [...this.assetObjects, newAsset]});

    newLottieAnimation = newLottieAnimation.updateAssetsByType(LottiePrecompositionModel, (assetModel) => {
      if (assetModel.isBackgroundItem) {
        return assetModel.addLayerObject(layer);
      } else {
        return assetModel;
      }
    });

    return this.updateAnimation(newLottieAnimation);
  }

  // FIXME: Doesn't work correctly due since it is not using the newer logic
  public setBackground(hexColor: string): this {
    return this.updateAnimation(
      this.lottieAnimation.updateAssetsByType(LottiePrecompositionModel, (assetModel) =>
        assetModel.updateLayersByType(LottieSolidColorLayerModel, (layerModel) => layerModel.changeSolidColor(hexColor))
      )
    );
  }

  public changeOpacity(value: number): this {
    return this.updateAnimation(
      this.lottieAnimation.updateAssetsByType(LottiePrecompositionModel, (assetModel) =>
        assetModel.updateLayersByType(LottieSolidColorLayerModel, (layerModel) => layerModel.changeOpacity(value))
      )
    );
  }

  private updateAnimation(newLottieAnimation: LottieAnimationModel): this {
    return this.update({lottieAnimation: newLottieAnimation.toObject()});
  }

  private getTextLayerDimensions(
    textComposition: LottiePrecompositionModel
  ): Nullable<{width: number; height: number}> {
    const parentComposition = this.lottieAnimation.preCompositionAssets.find((assetModel) =>
      assetModel.preCompositionLayers.isSome((layer) => layer.refId === textComposition.id)
    );

    if (!parentComposition) return null;

    const referenceLayer = parentComposition.preCompositionLayers.find((layer) => layer.refId === textComposition.id);

    if (!referenceLayer) return null;

    return {width: referenceLayer.width, height: referenceLayer.height};
  }
}

export namespace AnimatedSegmentModel {
  export interface IJson {
    id?: number;
    uuid: string;
    position: number;
    lottieAnimation: LottieAnimationModel.IJson;
    durationInFrames: number;
    hasAutoFitPhotoStrategy: boolean;
    hasDynamicPhotoEffects: boolean;
    hasDynamicBackground: boolean;
    isIntro: boolean;
  }
}
/* v8 ignore stop */
